/**
 * RiskMgmtHome — risk.mypcb.com home, redesigned 0.24.0 (Bill 2026-11-03) on the shared platform home.
 *
 *   Needs attention  the two review queues Risk Management decides, from their own reads:
 *                    Verifications — GET /v3/risk/verification/reviews?status=open
 *                    Sanctions     — GET /v3/risk/sanctions/reviews?status=open
 *   Common tasks     shell/riskNav.ts, shown when the person can open the page.
 *   Recent changes   the Risk settings change log, GET /v3/risk/settings/history (latest five).
 *
 * The approvals KPIs that sat here behind SHOW_UNPROVEN are gone with the old tile grid; those pages
 * are still listed in the menu whenever SHOW_UNPROVEN is switched on.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PlatformHome, ageText, oldest, whenText, type AttentionItem, type RecentChange, type SearchItem } from '@retail-ops/panel-shell';
import { ShellLink, useRiskNav } from '@/components/ShellLayout';
import { CHANGE_HISTORY_URL } from '@/shell/riskNav';
import { loadVerificationReviews } from '@/services/verificationReviewsApi';
import { loadReviews } from '@/services/sanctionsApi';
import { loadRiskHistory } from '@/services/riskSettingsApi';

const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

function useQueue(
  id: string,
  title: string,
  path: string,
  load: () => Promise<ReadonlyArray<{ at: string | null }>>,
): AttentionItem | null {
  const [item, setItem] = useState<AttentionItem | null>(null);
  useEffect(() => {
    let cancelled = false;
    void load()
      .then((rows) => {
        if (cancelled) return;
        const n = rows.length;
        const age = ageText(oldest(rows.map((r) => r.at)));
        setItem(
          n === 0
            ? { id, tone: 'ok', chip: 'All clear', title, text: 'Nothing is waiting for a decision.', path }
            : { id, tone: 'open', chip: `${n} open`, title, text: age ? `Waiting for a decision. The oldest has waited ${age}.` : 'Waiting for a decision.', path },
        );
      })
      .catch((e: unknown) => {
        if (!cancelled) setItem({ id, tone: 'neutral', chip: 'Could not check', title, text: errText(e), path });
      });
    return () => {
      cancelled = true;
    };
    // load is a stable module function per call site
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, path]);
  return item;
}

export function RiskMgmtHome(): JSX.Element {
  const { nav, tasks, searchItems } = useRiskNav();
  const navigate = useNavigate();
  const onPick = useCallback(
    (it: SearchItem) => {
      if (it.external) window.open(it.path, '_blank', 'noopener,noreferrer');
      else void navigate(it.path);
    },
    [navigate],
  );

  const verifications = useQueue('verifications', 'Identity verification holds', '/verifications', async () =>
    (await loadVerificationReviews('open')).map((r) => ({ at: r.runAt })),
  );
  const sanctions = useQueue('sanctions', 'Sanctions screenings held', '/sanctions', async () =>
    (await loadReviews('open')).map((r) => ({ at: r.screenedAt })),
  );
  const attentionItems = [verifications, sanctions].filter((x): x is AttentionItem => !!x);

  const [recent, setRecent] = useState<{ loading: boolean; error: string | null; items: RecentChange[] }>({ loading: true, error: null, items: [] });
  useEffect(() => {
    let cancelled = false;
    void loadRiskHistory(undefined, 5)
      .then((rows) => {
        if (cancelled) return;
        setRecent({
          loading: false,
          error: null,
          items: rows.map((c) => ({
            id: String(c.id),
            what: `${c.kind === 'gate' ? 'Gate' : 'Setting'} ${c.targetKey}: ${c.oldValue ?? '(default)'} → ${c.newValue ?? '(default)'}`,
            why: c.reason,
            who: c.changedBy,
            when: whenText(c.changedAt),
          })),
        });
      })
      .catch((e: unknown) => {
        if (!cancelled) setRecent({ loading: false, error: errText(e), items: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PlatformHome
      title="Risk Management"
      intro="Risk Management owns these settings and content for account opening. Changes apply to the next application; nothing already decided is re-graded."
      Link={ShellLink}
      searchItems={searchItems}
      onSearchPick={onPick}
      searchPlaceholder={'What do you want to do?  e.g. "clear a hold", "FraudPoint cut-off"'}
      attention={{ loading: attentionItems.length < 2, items: attentionItems }}
      tasks={tasks}
      tasksTour="risk-tiles"
      recent={{ ...recent, seeAllPath: '/risk-settings', seeAllLabel: 'Risk settings and their change log' }}
      footer={
        nav.sections.length > 0 ? (
          <p className="ps-muted">
            Every change across the panels, with restore, is in the{' '}
            <a href={CHANGE_HISTORY_URL} target="_blank" rel="noopener noreferrer">
              change history in the Admin portal
            </a>
            .
          </p>
        ) : null
      }
    />
  );
}
